/**
 * @author Trendrating <info@trendrating.net>
 *
 * @module api/account/Users
 * @summary Requests for users
 *
 */

import { _Base } from "./_Base";
import { endpoints } from "./endpoints";
import { decodeUser } from "./Decoder";

export class Users extends _Base {
  endPointRoot = process.env.REACT_APP_ROOT_T_REST;

  /**
   * Get users
   *
   * @param {object} params - request parameters
   * @param {Array}  params.ids - user ids to be retrieved
   * @param {Array}  params.properties - properties to be retrieved
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  get(params: any) {
    const url = this.endPointRoot + endpoints.accounts.users.get;

    const paramsGet: any = [
      {
        classType: "USER",
        extendedRequest: {
          onDemandResult: [],
        },
        id: params["ids"] != null ? params["ids"] : null,
      },
    ];
    for (const property of params["properties"]) {
      paramsGet[0]["extendedRequest"]["onDemandResult"].push({
        dimension: property,
      });
    }

    return this.preparePost(url, paramsGet, null);
  }

  async getForFiltering(filters: any, abortController: AbortController) {
    const url = this.endPointRoot + endpoints.accounts.users.getByIdsFilter;
    const _params: any = {
      searches: [{ filters: filters }],
    };
    const response = await this.preparePost(url, _params, null, abortController);
    return response;
  }

  async getForSearch(searches: any) {
    const url = this.endPointRoot + endpoints.accounts.users.getByIdsFilter;
    const _params: any = {
      searches: searches.map((item: any) => ({ filters: [item] })),
    };
    const response = await this.preparePost(url, _params, null);
    return response;
  }

  async getActiveUsers() {
    const url = this.endPointRoot + endpoints.accounts.users.getByIdsFilter;
    const _params: any = {
      searches: [
        {
          filters: [
            {
              dimension: "active",
              segments: [1],
            },
          ],
          sort: {
            dimension: "creationTime",
            rev: true,
          },
        },
      ],
    };
    const response = await this.preparePost(url, _params, null);
    return response;
  }

  async getInactiveUsers() {
    const url = this.endPointRoot + endpoints.accounts.users.getByIdsFilter;

    const _params: any = {
      searches: [
        {
          filters: [
            {
              dimension: "active",
              segments: [0],
            },
          ],
        },
      ],
    };

    const response = await this.preparePost(url, _params, null);
    return response;
  }

  async fetchUserProperties(properties: string[], ids: number[], abortController?: AbortController) {
    const url = this.endPointRoot + endpoints.common.fetch;
    const props = properties.map((prop) => ({
      dimension: prop,
    }));
    const params: any = {
      classType: "USER",
      id: ids,
      extendedRequest: {
        onDemandResult: props,
      },
    };

    const response = await this.preparePost(url, params, null, abortController);
    return response;
  }

  /**
   * Impersonate another user
   *
   * @param {number} userId - the ID of the user to impersonate
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  async impersonate(userId: any) {
    const url = this.endPointRoot + endpoints.accounts.users.impersonate;

    const response = await this.prepareGet(url, { id: userId }, null);
    return response.data.user;
  }

  async getUserPermissions(userID: number, headers: any) {
    const url = this.endPointRoot + endpoints.accounts.users.getUserPermissions;
    const params = { userId: userID };
    const response = await this.prepareGet(url, params, headers);
    return response;
  }

  /**
   * User login
   *
   * @param {object} params - login parameters
   * @param {string} params.email - user email
   * @param {string} params.password - user password
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  async login({ email, password }: any) {
    const urlCookies = this.endPointRoot + endpoints.accounts.sessions.remove;
    const urlLogin = this.endPointRoot + endpoints.accounts.users.login;

    const basicAuth = this._basicAuth(email, password);

    const headers = {
      Authorization: basicAuth,
    };

    /*const removeCookiesResponse =*/ await this.prepareGet(
      urlCookies,
      null,
      null
    );

    const response = await this.preparePost(
      urlLogin,
      { email, password },
      headers
    );

    //!--
    const _url = this.endPointRoot + endpoints.accounts.sessions.get;
    const _response = await this.prepareGet(_url, {
      testget: "check",
    });
    //!--

    const JSESSIONID = _response?.data?.JSESSIONID;
    response.data.JSESSIONID = JSESSIONID;

    const decodedUser: any = decodeUser(response);
    //get user permissions
    let permissions = await this.getUserPermissions(
      parseInt(decodedUser.id),
      basicAuth
    );
    if (permissions) {
      decodedUser["permissions"] = permissions.data.permissions;
    }
    return decodedUser;
  }

  /**
   * User logout
   *
   * @returns {Promise} a promise fulfilled with the
   *       handled data of the response
   */
  logout() {
    const url = this.endPointRoot + endpoints.accounts.users.logout;
    return this.prepareGet(url, null, null);
  }
  _basicAuth(email: string, password: string) {
    const token = email + ":" + password;
    const textEncoder = new TextEncoder();
    const hash = window.btoa(
      String.fromCharCode(...Array.from(textEncoder.encode(token)))
    );

    return "Basic " + hash;
  }

  // deleteUser(userID: any, dryRun: any) {
  //
  //   const url = this.endPointRoot + endpoints.accounts.users.delete;
  //   return this.prepareGet(url, { id: userID, dryRun: dryRun }, null);
  // }

  getGroups() {
    const url = this.endPointRoot + endpoints.accounts.users.groups;
    return this.prepareGet(url, null, null);
  }
  async getOwners() {
    const url = this.endPointRoot + endpoints.accounts.users.owners;
    const response = await this.prepareGet(url, null, null);

    return response;
  }
  async selectUsers(params: any) {
    const url = this.endPointRoot + endpoints.accounts.users.getByIdsFilter;

    const _params: any = {
      searches: [params],
    };

    const response = await this.preparePost(url, _params, null);
    return response;
  }
  async edit(accountParams: any) {
    const url = this.endPointRoot + endpoints.accounts.users.edit;
    const response = await this.preparePost(url, accountParams, null);
    return response;
  }
  async save(newAccountParams: any) {
    const url = this.endPointRoot + endpoints.accounts.users.save;
    const response = await this.preparePost(url, newAccountParams, null);
    return response;
  }
  async sessions() {
    const url = this.endPointRoot + endpoints.accounts.users.sessions;
    const response = await this.prepareGet(url, null, null);
    return response;
  }
}
